import { DeleteOutlined, InboxOutlined } from "@ant-design/icons";
import { BriefDeliverableTaskUploadTypeEnum } from "@entities/brief/lib/constants";
import { useGetDraftById } from "@entities/brief/lib/hooks/darfting/useGetDraftById";
import { useUploadTaskContent } from "@entities/campaigns/lib/hooks/useUploadTaskContent";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { Button } from "@shared/ui/Button";
import { Flex, Form, Input, Radio, UploadFile } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { Youtube } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";

interface UploadModalProps {
  taskId: string;
  campaignInfluencerId: string;
  deliverableConfigRefId: string;
  onClose: () => void;
  draftingId?: string;
  // setLatestVersionId: (versionId: string) => void;
  refetch: () => void;
  briefId?: string;
}

const UploadModal: React.FC<UploadModalProps> = ({
  refetch,
  onClose,
  taskId,
  campaignInfluencerId,
  deliverableConfigRefId,
  // setLatestVersionId,
  draftingId,
  briefId,
}) => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState<"file" | "youtube" | "link">(
    "file"
  );
  const navigate = useNavigate();

  const { drafting } = useGetDraftById(draftingId || "");

  const {
    uploadTaskContent,
    isUploaded,
    isUploading,
    latestVersionId,
    draftId: latestDraftId,
  } = useUploadTaskContent(briefId || drafting?.drafting?.briefId || "");

  const [files, setFiles] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (isUploaded && latestVersionId && latestDraftId) {
      ToastService.success("Version uploaded successfully.");

      refetch();

      // setLatestVersionId(latestVersionId);
      // navigate(
      //   ROUTES.DRAFTING.replace(
      //     ":draftingId",
      //     latestDraftId + "?draftingVersion=" + latestVersionId
      //   )
      // );
      form.resetFields();
      onClose();
    }
  }, [isUploaded, latestVersionId, latestDraftId]);

  /** ----------------------------------------
   *  FILE UPLOAD PROPS (Firebase Upload)
   * ----------------------------------------*/
  const uploadProps = {
    multiple: true,
    customRequest: async (options: any) => {
      const { file, onSuccess, onError } = options;
      try {
        setFiles(form.getFieldValue("files")); // take value before upload
        const firebaseUrl = await FirebaseService.uploadFileToFirebase(
          `draft/${Date.now()}`,
          file
        );
        onSuccess(firebaseUrl);
        setFiles(form.getFieldValue("files")); // take value after upload
      } catch (err) {
        onError(err);
      }
    },
    accept:
      ".mp4, .mov, .jpg, .png, image/jpeg, image/png, video/mp4, video/quicktime,image/gif, image/webp",
  };

  /** ----------------------------------------
   *  HANDLE FORM SUBMISSION
   * ----------------------------------------*/
  const handleSubmit = async (values: any) => {
    /** ---------------- FILE UPLOADS ---------------- */
    if (activeTab === "file" && values.files) {
      try {
        const payload = {
          campaignInfluencerId,
          deliverableConfigRefId,
          taskId,
          contentUrls: values?.files?.map((file: any) => ({
            ...file,
            url: file?.response,
            type:
              file.type.includes("image") ||
              file.type.includes("webp") ||
              file.type.includes("png") ||
              file.type.includes("jpeg") ||
              file.type.includes("jpg")
                ? BriefDeliverableTaskUploadTypeEnum.Image
                : BriefDeliverableTaskUploadTypeEnum.Video,
          })),
        };

        uploadTaskContent({ data: payload });
      } catch (e) {
        ToastService.error("Failed to submit. Please try again.");
      }
    }

    /** ---------------- MULTIPLE YOUTUBE LINKS ---------------- */
    if (activeTab === "youtube" && values.youtubeLinks?.length) {
      try {
        const payload = {
          campaignInfluencerId,
          deliverableConfigRefId,
          taskId,
          contentUrls: values?.youtubeLinks?.map((item: any) => ({
            ...item,
            url: item?.url,
            type: BriefDeliverableTaskUploadTypeEnum.YoutubeLink,
          })),
        };

        uploadTaskContent({ data: payload });
      } catch (e) {
        ToastService.error("Failed to submit. Please try again.");
      }
    }

    /** ---------------- MULTIPLE DIRECT FILE LINKS ---------------- */
    if (activeTab === "link" && values.fileLinks?.length) {
      try {
        const payload = {
          campaignInfluencerId,
          deliverableConfigRefId,
          taskId,
          contentUrls: values?.fileLinks?.map((item: any) => ({
            ...item,
            url: item?.url,
            type: BriefDeliverableTaskUploadTypeEnum.MediaLink,
          })),
        };

        uploadTaskContent({ data: payload });
      } catch (e) {
        ToastService.error("Failed to submit. Please try again.");
      }
    }
  };

  const isDisabled =
    files?.filter((file) => file.status === "uploading").length > 0;

  return (
    <div>
      {/* TABS */}
      <Radio.Group
        options={[
          { label: "File Upload", value: "file" },
          { label: "YouTube Links", value: "youtube" },
          { label: "File Links", value: "link" },
        ]}
        value={activeTab}
        className={styles.radioGroup}
        onChange={(e) => {
          setActiveTab(e.target.value);
          form.resetFields();
        }}
        optionType="button"
        buttonStyle="solid"
        style={{ marginBottom: 16 }}
      />

      <Form
        form={form}
        requiredMark={false}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          fileLinks: [
            {
              url: "",
            },
          ],
          youtubeLinks: [
            {
              url: "",
            },
          ],
        }}
      >
        {/* -------------------- FILE UPLOAD -------------------- */}
        {activeTab === "file" && (
          <Form.Item
            name="files"
            label="Upload Files"
            valuePropName="fileList"
            getValueFromEvent={(e) => e.fileList}
            rules={[
              { required: true, message: "Please upload at least one file" },
            ]}
          >
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag files here</p>
              <p className="ant-upload-hint">MP4, MOV, JPG, PNG</p>
            </Dragger>
          </Form.Item>
        )}

        {/* -------------------- MULTIPLE YOUTUBE LINKS -------------------- */}
        {activeTab === "youtube" && (
          <Form.List
            name="youtubeLinks"
            rules={[
              {
                validator: (_, values) => {
                  if (values.length < 1) {
                    return Promise.reject(
                      new Error(
                        "Please enter at least one youtube link to continue."
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field) => (
                  <Flex
                    key={field.key}
                    align="baseline"
                    gap={5}
                    style={{ marginBottom: 8 }}
                  >
                    <Form.Item
                      {...field}
                      name={[field.name, "url"]}
                      rules={[
                        { required: true, message: "Enter YouTube URL" },
                        { type: "url", message: "Invalid URL" },
                      ]}
                      style={{
                        flex: 1,
                        color: "gray !important",
                        fontWeight: 700,
                        fontSize: "8px !important",
                      }}
                      label="YOUTUBE LINK"
                    >
                      <Input
                        prefix={<Youtube size={16} style={{ opacity: 0.6 }} />}
                        placeholder="https://youtube.com/watch?v=..."
                      />
                    </Form.Item>

                    <DeleteOutlined
                      onClick={() => remove(field.name)}
                      style={{ color: "red", cursor: "pointer" }}
                    />
                  </Flex>
                ))}

                <Button style={{ marginBottom: 12 }} onClick={() => add()}>
                  + Add YouTube Link
                </Button>

                <Form.Item style={{ margin: 0 }}>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
        )}

        {/* -------------------- MULTIPLE FILE LINKS -------------------- */}
        {activeTab === "link" && (
          <Form.List
            name="fileLinks"
            rules={[
              {
                validator: (_, values) => {
                  if (values.length < 1) {
                    return Promise.reject(
                      new Error(
                        "Please enter at least one file link to continue."
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field) => (
                  <Flex
                    key={field.key}
                    align="baseline"
                    style={{ display: "flex", marginBottom: 8 }}
                    gap={5}
                  >
                    <Form.Item
                      {...field}
                      label="FILE LINK"
                      name={[field.name, "url"]}
                      rules={[
                        { required: true, message: "Enter file link" },
                        { type: "url", message: "Invalid URL" },
                      ]}
                      style={{
                        flex: 1,
                        color: "gray !important",
                        fontWeight: 700,
                        fontSize: "8px !important",
                      }}
                    >
                      <Input placeholder="https://example.com/myfile.mp4" />
                    </Form.Item>

                    <DeleteOutlined
                      onClick={() => remove(field.name)}
                      style={{ color: "red", cursor: "pointer" }}
                    />
                  </Flex>
                ))}

                <Button onClick={() => add()} style={{ marginBottom: 12 }}>
                  + Add File Link
                </Button>
                <Form.Item style={{ margin: 0 }}>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
        )}

        {/* SUBMIT BUTTON */}
        <Button
          htmlType="submit"
          // style={{
          //   width: "100%",
          //   background: "#1f6feb",
          //   color: "#fff",
          //   padding: "10px",
          //   fontWeight: 600,
          //   borderRadius: 8,
          // }}
          disabled={isDisabled}
          style={{
            backgroundColor: isDisabled
              ? "rgba(79, 70, 229, 0.5)"
              : "rgb(79, 70, 229)",
            cursor: isDisabled ? "not-allowed" : "pointer",
            opacity: isDisabled ? 0.6 : 1,
          }}
          type="primary"
          block
          loading={isUploading}
        >
          Upload
        </Button>
      </Form>
    </div>
  );
};

export default UploadModal;
